import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useBasePath from '../../utils/useBasePath';
import useGoalProgress from '../../hooks/useGoalProgress';
import { useAuth } from '../../contexts/AuthContext';
import { PORTAL_KEYS, PORTAL_META } from '../../contexts/MarketContext';
import GoalsEditor from '../../components/GoalsEditor';
import {
  monthLabel, overheadLabel, travellersOf, seasonMonthOf, expectedToDate,
} from '../../constants/goals';

const COPY = {
  en: {
    title: (s) => `${s.label} Season Goals`,
    sub: (s) => `Tours and travellers needed to cover the ${overheadLabel(s.monthlyOverhead)} monthly overhead. A tour counts once its deposit is paid, in the month it departs.`,
    edit: 'Edit goals', includes: 'Includes',
    portalNames: { uk_to_spain: 'UK to Spain', spain_to_uk: 'Spain to UK', padelante: 'Padelante' },
    toursWon: 'Tours won', travellersWon: 'Travellers won',
    toursTarget: 'Tours', travellersTarget: 'Travellers',
    remaining: 'still needed', complete: 'Target met',
    seasonTotal: 'Season total', perMonth: 'per month', acrossSeason: 'across the season',
    monthly: 'Monthly pace', seasonPaced: 'Season total',
    onTrack: 'On track', behind: 'Behind pace', ahead: 'Ahead of pace',
    notStarted: 'Season has not started yet',
    startsIn: (m) => `Season starts ${m}`,
    breakdown: 'Month by month',
    tours: 'tours', travellers: 'travellers',
    noneYet: 'No deposit-paid tours in the season yet.',
    empty: 'No goals apply to this portal.',
    loading: 'Loading…',
    overallTours: 'Tours', overallTravellers: 'Travellers',
    ofTarget: 'of target',
  },
  es: {
    title: (s) => `Objetivos de la temporada ${s.label}`,
    sub: (s) => `Tours y viajeros necesarios para cubrir los ${overheadLabel(s.monthlyOverhead)} de gastos fijos mensuales. Un tour cuenta cuando se paga el depósito, en el mes de salida.`,
    edit: 'Editar objetivos', includes: 'Incluye',
    portalNames: { uk_to_spain: 'Reino Unido a España', spain_to_uk: 'España a Reino Unido', padelante: 'Padelante' },
    toursWon: 'Tours conseguidos', travellersWon: 'Viajeros conseguidos',
    toursTarget: 'Tours', travellersTarget: 'Viajeros',
    remaining: 'aún necesarios', complete: 'Objetivo cumplido',
    seasonTotal: 'Total temporada', perMonth: 'al mes', acrossSeason: 'en la temporada',
    monthly: 'Ritmo mensual', seasonPaced: 'Total temporada',
    onTrack: 'En objetivo', behind: 'Por debajo', ahead: 'Por encima',
    notStarted: 'La temporada aún no ha empezado',
    startsIn: (m) => `La temporada empieza en ${m}`,
    breakdown: 'Mes a mes',
    tours: 'tours', travellers: 'viajeros',
    noneYet: 'Aún no hay tours con depósito pagado en la temporada.',
    empty: 'No hay objetivos para este portal.',
    loading: 'Cargando…',
    overallTours: 'Tours', overallTravellers: 'Viajeros',
    ofTarget: 'del objetivo',
  },
};

function pct(done, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

/** Horizontal progress bar with the target as the full width. */
function Bar({ done, total, color, expected }) {
  const p = pct(done, total);
  const e = expected != null && total > 0 ? Math.min(100, (expected / total) * 100) : null;
  return (
    <div className="relative h-[8px] rounded-full bg-[#EDEFF2] overflow-hidden">
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${p}%`, background: color, transition: 'width .5s' }}
      />
      {/* Pace marker — where this category should be by now */}
      {e != null && e > 0 && e < 100 && (
        <div
          className="absolute inset-y-0 w-[2px] bg-[#0D2240]/45"
          style={{ left: `${e}%` }}
          title="Where this should be by now"
        />
      )}
    </div>
  );
}

function Metric({ label, done, total, unit, color, expected, L }) {
  const remaining = Math.max(0, total - done);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">{label}</span>
        <span className="text-[12px] text-[#6B7280] tabular-nums">
          <strong className="text-[15px] font-extrabold text-[#111827]">{done}</strong>
          <span className="mx-1">/</span>{total}
        </span>
      </div>
      <Bar done={done} total={total} color={color} expected={expected} />
      <div className="mt-1.5 text-[12px] text-[#6B7280]">
        {remaining > 0
          ? <>{remaining} {unit} {L.remaining}</>
          : <span className="text-sg font-bold">{L.complete}</span>}
      </div>
    </div>
  );
}

function PaceChip({ done, expected, L }) {
  if (expected == null || expected <= 0) return null;
  const diff = done - expected;
  const tol = Math.max(0.5, expected * 0.1);
  const state = diff >= tol ? 'ahead' : diff <= -tol ? 'behind' : 'on';
  const style = {
    ahead:  { bg: '#D1FAE5', fg: '#065F46', text: L.ahead },
    on:     { bg: '#EBF2FC', fg: '#1A4A8A', text: L.onTrack },
    behind: { bg: '#FEE2E2', fg: '#991B1B', text: L.behind },
  }[state];
  return (
    <span
      className="text-[11px] font-bold px-2 py-[3px] rounded-full whitespace-nowrap"
      style={{ background: style.bg, color: style.fg }}
    >
      {style.text}
    </span>
  );
}

export default function GoalsPage({ locale = 'en' }) {
  const L = COPY[locale] || COPY.en;
  const base = useBasePath();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { goals, data, totals, elapsed, season, months, rawConfig, config, loading } = useGoalProgress();
  const [editing, setEditing] = useState(false);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><p className="text-[13px] text-[#9CA3AF]">{L.loading}</p></div>;
  }

  const editButton = isAdmin && (
    <button
      onClick={() => setEditing(true)}
      className="flex-shrink-0 px-[14px] py-[8px] text-[13px] font-bold text-pb border border-pb/30 hover:bg-blue-light rounded-[9px] transition"
    >
      {L.edit}
    </button>
  );
  const editor = editing && (
    <GoalsEditor raw={rawConfig} config={config} onClose={() => setEditing(false)} />
  );

  if (!goals.length) {
    return (
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-[18px] font-bold text-[#111827]">{L.title(season)}</h2>
          {editButton}
        </div>
        <p className="text-[13px] text-[#9CA3AF]">{L.empty}</p>
        {editor}
      </div>
    );
  }

  const monthCols = months;
  // Cards are grouped by portal; the heading only earns its place when the
  // viewer can see more than one portal's goals.
  const portalGroups = PORTAL_KEYS
    .map((p) => ({ portal: p, goals: goals.filter((g) => g.portal === p) }))
    .filter((grp) => grp.goals.length);
  const showPortalHeadings = portalGroups.length > 1;

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[18px] font-bold text-[#111827]">{L.title(season)}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1 max-w-[760px]">{L.sub(season)}</p>
        </div>
        {editButton}
      </div>
      {editor}

      {/* ── Season summary ── */}
      <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-5">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div>
            <div className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">
              {monthLabel(season.startMonth)} – {monthLabel(season.endMonth)}
            </div>
            <div className="text-[13px] text-[#6B7280]">
              {elapsed === 0
                ? L.startsIn(monthLabel(season.startMonth))
                : `${L.breakdown} · ${elapsed}/${months.length}`}
            </div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">{L.overallTours}</span>
              <span className="text-[12px] text-[#6B7280] tabular-nums">
                <strong className="text-[15px] font-extrabold text-[#111827]">{totals.tDone}</strong>
                <span className="mx-1">/</span>{totals.tTarget}
                <span className="ml-2 text-[#9CA3AF]">{pct(totals.tDone, totals.tTarget)}% {L.ofTarget}</span>
              </span>
            </div>
            <Bar done={totals.tDone} total={totals.tTarget} color="#0D2240" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">{L.overallTravellers}</span>
              <span className="text-[12px] text-[#6B7280] tabular-nums">
                <strong className="text-[15px] font-extrabold text-[#111827]">{totals.pDone}</strong>
                <span className="mx-1">/</span>{totals.pTarget}
                <span className="ml-2 text-[#9CA3AF]">{pct(totals.pDone, totals.pTarget)}% {L.ofTarget}</span>
              </span>
            </div>
            <Bar done={totals.pDone} total={totals.pTarget} color="#0D2240" />
          </div>
        </div>
      </div>

      {/* ── Per-category cards, grouped by portal ── */}
      {portalGroups.map((grp) => (
      <div key={grp.portal} className="space-y-3">
        {showPortalHeadings && (
          <h3 className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.9px] pt-1">
            {L.portalNames[grp.portal] || PORTAL_META[grp.portal]?.label || grp.portal}
          </h3>
        )}
      <div className="grid grid-cols-1 1080:grid-cols-2 gap-4">
        {grp.goals.map((g) => {
          const d = data[g.key] || { tours: 0, travellers: 0 };
          const exp = expectedToDate(g, elapsed);
          return (
            <div key={g.key} className="rounded-[12px] border border-[#E5E7EB] bg-white p-5 space-y-4">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0"
                  style={{ background: `${g.color}1A` }}>
                  <span className="w-[14px] h-[14px] rounded-full" style={{ background: g.color }} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-bold text-[#111827] truncate">{g.label}</div>
                  <div className="text-[12px] text-[#9CA3AF]">
                    {g.pace === 'monthly'
                      ? `${g.toursPerMonth} ${L.tours} · ${g.travellersPerMonth} ${L.travellers} ${L.perMonth}`
                      : `${g.tours} ${L.tours} · ${g.travellersPerTour} ${L.travellers}/tour ${L.acrossSeason}`}
                  </div>
                  {g.groupTypes.length > 0 && (
                    <div className="text-[11.5px] text-[#6B7280] mt-0.5 truncate">
                      {L.includes} {g.groupTypes.join(', ')}
                    </div>
                  )}
                </div>
                {/* Pace compares only what has already departed — see aggregateGoals */}
                {exp && <PaceChip done={d.toDate?.tours ?? 0} expected={exp.tours} L={L} />}
              </div>

              <Metric label={L.toursTarget} done={d.tours} total={g.tours} unit={L.tours}
                color={g.color} expected={exp?.tours} L={L} />
              <Metric label={L.travellersTarget} done={d.travellers} total={g.travellers} unit={L.travellers}
                color={g.color} expected={exp?.travellers} L={L} />
            </div>
          );
        })}
      </div>
      </div>
      ))}

      {/* ── Month-by-month grid ── */}
      <div className="rounded-[12px] border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#E5E7EB]">
          <h3 className="text-[14px] font-bold text-[#111827]">{L.breakdown}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-[#F9FAFB]">
                <th className="text-left px-5 py-2.5 text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] sticky left-0 bg-[#F9FAFB]">
                  &nbsp;
                </th>
                {monthCols.map((m) => (
                  <th key={m} className="px-2 py-2.5 text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] whitespace-nowrap">
                    {monthLabel(m)}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px] whitespace-nowrap border-l border-[#E5E7EB]">
                  {L.seasonTotal}
                </th>
              </tr>
            </thead>
            <tbody>
              {goals.map((g) => {
                const d = data[g.key];
                const monthTarget = g.pace === 'monthly' ? g.toursPerMonth : null;
                return (
                  <tr key={g.key} className="border-t border-[#F3F4F6]">
                    <td className="px-5 py-3 sticky left-0 bg-white">
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span className="w-[9px] h-[9px] rounded-full flex-shrink-0" style={{ background: g.color }} />
                        <span className="text-[13px] font-semibold text-[#374151]">{g.label}</span>
                      </div>
                    </td>
                    {monthCols.map((m) => {
                      const cell = d.months[m];
                      const hit = monthTarget != null && cell.tours >= monthTarget;
                      return (
                        <td key={m} className="px-2 py-3 text-center">
                          {cell.tours === 0 ? (
                            <span className="text-[13px] text-[#D1D5DB]">–</span>
                          ) : (
                            <div className="inline-flex flex-col items-center leading-tight">
                              <span
                                className="text-[13px] font-extrabold tabular-nums"
                                style={{ color: hit ? '#1A7A4A' : '#111827' }}
                              >
                                {cell.tours}
                              </span>
                              <span className="text-[11px] text-[#9CA3AF] tabular-nums">{cell.travellers}</span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-center border-l border-[#E5E7EB]">
                      <div className="inline-flex flex-col items-center leading-tight">
                        <span className="text-[13px] font-extrabold text-[#111827] tabular-nums">
                          {d.tours}<span className="text-[#9CA3AF] font-semibold">/{g.tours}</span>
                        </span>
                        <span className="text-[11px] text-[#9CA3AF] tabular-nums">
                          {d.travellers}/{g.travellers}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-2.5 border-t border-[#E5E7EB] bg-[#F9FAFB] text-[11px] text-[#9CA3AF]">
          {L.tours} / {L.travellers}
        </div>
      </div>

      {/* ── Contributing tours ── */}
      <div className="rounded-[12px] border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#E5E7EB]">
          <h3 className="text-[14px] font-bold text-[#111827]">{L.toursWon}</h3>
        </div>
        {totals.tDone === 0 ? (
          <p className="px-5 py-6 text-[13px] text-[#9CA3AF]">{L.noneYet}</p>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            {goals.flatMap((g) =>
              (data[g.key]?.list || [])
                .slice()
                .sort((a, b) => (a.departureDate || '').localeCompare(b.departureDate || ''))
                .map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`${base}/requests/${r.id}`)}
                    className="w-full flex items-center gap-3 px-5 py-3 hover:bg-[#F9FAFB] transition text-left"
                  >
                    <span className="w-[9px] h-[9px] rounded-full flex-shrink-0" style={{ background: g.color }} />
                    <span className="flex-1 min-w-0 text-[13px] font-semibold text-[#374151] truncate">
                      {r.internalRef || r.tourName || `${r.sport || ''} — ${r.destination || ''}`}
                    </span>
                    <span className="text-[12px] text-[#9CA3AF] whitespace-nowrap">{g.label}</span>
                    <span className="text-[12px] text-[#6B7280] tabular-nums whitespace-nowrap w-[70px] text-right">
                      {monthLabel(seasonMonthOf(r))}
                    </span>
                    <span className="text-[12px] font-bold text-[#111827] tabular-nums whitespace-nowrap w-[70px] text-right">
                      {travellersOf(r)} {L.travellers}
                    </span>
                  </button>
                ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
